import { useEffect, useState } from "react";
import { api } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Download, FileText, ArrowUpRight, ArrowDownRight, Minus, Info } from "lucide-react";
import { MdText, YearlyPerformance } from "../components/YearlyPerformance";
import { SignalsOpsTable } from "../components/SignalsOpsTable";
import { EquityChart } from "../components/TradesTable";
import { PriceChart } from "../components/PriceChart";

const POS_COLOR = { LONG: "text-emerald-400", SHORT: "text-red-400", FLAT: "text-white" };

function InstructionBanner() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/signals/today").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data?.signal || !data.show_instruction || !data.signal.instruction) return null;
  const sig = data.signal;
  const cls = sig.position === "LONG"
    ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
    : sig.position === "SHORT"
      ? "border-red-500/60 bg-red-500/10 text-red-300"
      : "border-white/40 bg-white/5 text-white";
  const Icon = sig.position === "LONG" ? ArrowUpRight : sig.position === "SHORT" ? ArrowDownRight : Minus;
  return (
    <div data-testid="instruction-banner" className={`border ${cls} p-6 mb-10`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-xs tracking-[0.25em] uppercase opacity-80">{t("instr_title")}</span>
        <span className="text-xs font-mono opacity-60">{sig.date} · {t("published_from")}{data.realtime ? "" : ` · ${t("signal_delayed")}`}</span>
      </div>
      <div className="flex items-center gap-4 mt-3">
        <Icon className="w-12 h-12" />
        <div>
          <p data-testid="instruction-code" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
            {sig.instruction.replace("_SR", " S&R")}
          </p>
          <p data-testid="instruction-text" className="text-sm mt-1 opacity-90">
            {t(`instr_${sig.instruction}`)}
            {sig.tp_points ? ` · ${t("tp_note")} ${sig.tp_points} pt` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

function SignalsHistory() {
  const { t } = useLang();
  const [signals, setSignals] = useState([]);
  useEffect(() => { api.get("/signals", { params: { ops: 1 } }).then((r) => setSignals(r.data.signals)).catch(() => {}); }, []);
  if (!signals.length) return null;
  return (
    <div data-testid="signals-history" className="mt-12">
      <h2 className="text-base md:text-lg font-medium text-white mb-4">{t("signals_history")}</h2>
      <SignalsOpsTable signals={signals} />
    </div>
  );
}

function FeaturedReport({ report }) {
  const { lang, t } = useLang();
  if (!report) return null;
  const border = report.position === "LONG" ? "border-emerald-500/40" : report.position === "SHORT" ? "border-red-500/40" : "border-white/25";
  return (
    <div data-testid="featured-report" className={`border ${border} bg-[#111111] p-6 sm:p-8 mb-10`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <span className="text-xs tracking-[0.25em] uppercase text-slate-400">{t("featured_report")}</span>
        <span className={`text-xs font-mono px-2 py-0.5 border ${
          report.position === "LONG" ? "border-emerald-500/40 text-emerald-400"
            : report.position === "SHORT" ? "border-red-500/40 text-red-400" : "border-white/25 text-white"}`}>
          {report.position || "—"}
        </span>
      </div>
      <MdText text={lang === "it" ? report.content_it : report.content_en} />
    </div>
  );
}

function CurrentYearEquity() {
  const { lang } = useLang();
  const year = new Date().getFullYear();
  const [data, setData] = useState([]);
  useEffect(() => {
    api.get("/public/equity", { params: { year } }).then((r) => setData(r.data)).catch(() => {});
  }, [year]);
  if (!data.length) return null;
  return (
    <div data-testid="current-year-equity" className="mt-10">
      <EquityChart data={data}
        title={lang === "it" ? `Equity line ${year} — anno in corso (1 contratto Bund)` : `${year} equity line — current year (1 Bund contract)`} />
    </div>
  );
}

function DelayNote() {
  const { t } = useLang();
  const { user } = useAuth();
  const access = user?.access_level || "free";
  const status = access === "trial" ? t("delay_status_trial")
    : access === "free" ? t("delay_status_free") : t("delay_status_live");
  return (
    <div data-testid="delay-note" className="border border-white/10 bg-[#111111] p-6">
      <div className="flex items-center gap-2 mb-3">
        <Info className="w-4 h-4 text-emerald-400" />
        <h3 className="text-sm font-medium text-white">{t("delay_note_title")}</h3>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed max-w-3xl">{t("delay_note_text")}</p>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <span data-testid="delay-status" className={`text-xs font-mono px-3 py-1.5 border ${
          access === "subscriber" || access === "admin" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-300"}`}>
          {status}
        </span>
        {access !== "subscriber" && access !== "admin" && (
          <Link to="/pricing" data-testid="delay-note-cta"
            className="text-xs font-medium bg-emerald-500 hover:bg-emerald-400 text-black rounded px-4 py-1.5 transition-colors">
            {t("unlock")}
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Reports() {
  const { lang, t } = useLang();
  const [reports, setReports] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get("/public/reports", { params: { type: "daily", limit: 1 } })
      .then((r) => setFeatured(r.data.reports[0] || null)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/public/reports", { params: filter ? { type: filter } : {} })
      .then((r) => setReports(r.data.reports));
  }, [filter]);

  const download = (rep) => {
    const content = lang === "it" ? rep.content_it : rep.content_en;
    const blob = new Blob([content], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bundstrategy_${rep.type}_${rep.date}.md`;
    a.click();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <InstructionBanner />
      <FeaturedReport report={featured} />
      <DelayNote />
      <CurrentYearEquity />
      <div className="mt-6"><PriceChart /></div>
      <h1 className="font-heading text-4xl font-extrabold tracking-tighter text-white mt-10">{t("reports_title")}</h1>
      <p className="text-slate-400 mt-2">{t("reports_sub")}</p>
      <div className="flex gap-2 mt-8">
        {[["", "all"], ["daily", "daily"], ["weekly", "weekly"]].map(([v, label]) => (
          <button key={label} data-testid={`reports-filter-${label}`} onClick={() => setFilter(v)}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${filter === v ? "border-emerald-500/50 text-emerald-400" : "border-white/10 text-slate-400 hover:text-white"}`}>
            {t(label)}
          </button>
        ))}
      </div>
      <div className="mt-6 space-y-2">
        {reports.filter((rep) => rep.id !== featured?.id).map((rep) => (
          <div key={rep.id} data-testid={`report-item-${rep.id}`} className="border border-white/10 bg-[#111111]">
            <div className="flex items-center gap-3 px-4 py-3">
              <FileText className={`w-4 h-4 ${POS_COLOR[rep.position] || (rep.type === "weekly" ? "text-amber-400" : "text-emerald-400")}`} />
              <button data-testid={`report-open-${rep.id}`} onClick={() => setOpenId(openId === rep.id ? null : rep.id)}
                className="flex-1 text-left text-sm text-slate-200 hover:text-white transition-colors">
                {lang === "it" ? rep.title_it : rep.title_en}
                {rep.edited && <span className="ml-2 text-[10px] text-amber-400 uppercase">{t("signal_edited")}</span>}
              </button>
              <button data-testid={`report-download-${rep.id}`} onClick={() => download(rep)}
                className="text-slate-400 hover:text-white transition-colors"><Download className="w-4 h-4" /></button>
            </div>
            {openId === rep.id && (
              <pre className="px-4 pb-4 text-xs text-slate-300 whitespace-pre-wrap font-mono leading-relaxed border-t border-white/5 pt-3">
                {lang === "it" ? rep.content_it : rep.content_en}
              </pre>
            )}
          </div>
        ))}
      </div>
      <div className="mt-12"><YearlyPerformance /></div>
      <SignalsHistory />
    </div>
  );
}
